/**
 * Route Guards - Shared authentication/permission route wrappers.
 *
 * Extracted from App.tsx for reuse by all UI modes.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, AppPermissions } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// ============================================================
// ProtectedRoute
// ============================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">{t('buttons.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// ============================================================
// AdminRoute
// ============================================================

export const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">{t('buttons.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('permissions.accessDenied')}</h1>
          <p className="text-gray-600 mb-4">{t('permissions.adminRequired')}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('permissions.goToVoice')}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// ============================================================
// PermissionRoute
// ============================================================

interface PermissionRouteProps {
  permission: keyof AppPermissions;
  children: React.ReactNode;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({ permission, children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">{t('buttons.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const permissions: AppPermissions = user?.app_permissions || {
    calendar: true,
    email: true,
    knowledge_base: true,
    cogito: true,
    tasks: true,
    disk: true,
    people: true,
    chat: true,
    contractors: true
  };
  const hasPermission = permissions[permission] !== false;

  if (!hasPermission) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('permissions.accessDenied')}</h1>
          <p className="text-gray-600 mb-6">
            {t('permissions.moduleDisabled')}
            <br />
            {t('permissions.contactAdmin')}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('permissions.goToVoice')}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
